
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { NotificationCard } from "./NotificationCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Notification {
  id: string;
  title: string;
  description: string;
  created_at: string;
  read: boolean;
  type: 'progress_update' | 'reminder' | 'suggestion' | 'achievement';
}

export const NotificationList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("all");

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const { data: session } = await supabase.auth.getSession();
        if (!session.session) {
          navigate('/login');
          return;
        }

        const { data, error } = await supabase
          .from('notifications')
          .select('*')
          .eq('user_id', session.session.user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Validate and type cast the notification data
        const typedNotifications = (data || []).map(notification => {
          // Ensure type is one of the valid values, default to 'progress_update' if invalid
          const validTypes = ['progress_update', 'reminder', 'suggestion', 'achievement'] as const;
          const type = validTypes.includes(notification.type as any) 
            ? notification.type as 'progress_update' | 'reminder' | 'suggestion' | 'achievement'
            : 'progress_update';

          return {
            ...notification,
            type,
            read: notification.read || false
          } as Notification;
        });

        setNotifications(typedNotifications);
      } catch (error) {
        console.error('Error fetching notifications:', error);
        toast({
          title: "Error fetching notifications",
          description: "There was a problem loading your notifications",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();

    const channel = supabase
      .channel('notifications_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications'
        },
        () => {
          fetchNotifications();
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [navigate, toast]);

  const markAsRead = async (id: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', id);

      if (error) throw error;

      setNotifications(notifications.map(notification => 
        notification.id === id 
          ? { ...notification, read: true }
          : notification
      ));

      toast({
        title: "Notification marked as read",
        description: "This notification has been marked as read",
        duration: 2000,
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast({
        title: "Error",
        description: "Failed to mark notification as read",
        variant: "destructive",
      });
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setNotifications(notifications.filter(notification => notification.id !== id));
      
      toast({
        title: "Notification deleted",
        description: "The notification has been removed",
        duration: 2000,
      });
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast({
        title: "Error",
        description: "Failed to delete notification",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        <p className="mt-2 text-gray-600">Loading notifications...</p>
      </div>
    );
  }

  const filteredNotifications = notifications.filter(notification => {
    if (filter === "all") return true;
    return notification.type === filter;
  });

  if (notifications.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No notifications to display
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Select
          value={filter}
          onValueChange={setFilter}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Notifications</SelectItem>
            <SelectItem value="progress_update">Progress Updates</SelectItem>
            <SelectItem value="reminder">Reminders</SelectItem>
            <SelectItem value="suggestion">Suggestions</SelectItem>
            <SelectItem value="achievement">Achievements</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filteredNotifications.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No notifications found for the selected filter
        </div>
      ) : (
        <div className="space-y-4">
          {filteredNotifications.map((notification) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              onMarkAsRead={markAsRead}
              onDelete={deleteNotification}
            />
          ))}
        </div>
      )}
    </div>
  );
};
